<script lang="ts">
/**
 * Camera-style control bar: mic toggle, shutter button, QR scan toggle.
 *
 * @remarks
 * The shutter uses pointer events for dual-action interaction:
 * - **Tap** (< 500ms) — fires `capture` to take a photo
 * - **Long press** (>= 500ms) — fires `analyze` as a power-user shortcut
 *   (only when photos have already been taken)
 *
 * Laid out by the parent's flex container — no absolute positioning on the
 * root element. The parent (CaptureShell) controls where this sits.
 */
export interface CaptureControlsProps {
  /** Whether voice pre-hint recording is active. Controls mic button icon/color. */
  voiceActive: boolean
  /** Whether any photos have been captured. Gates the long-press analyze shortcut. */
  hasPhotos: boolean
  /** Whether CameraX is currently saving a photo. */
  captureBusy?: boolean
}
</script>

<script setup lang="ts">
import { ref } from "vue"

import { ICON } from "@/lib/icons"

const props = withDefaults(defineProps<CaptureControlsProps>(), { captureBusy: false })

const emit = defineEmits<{
  /** Fired on short press (tap) of the shutter button. */
  capture: []
  /** Fired on long press of the shutter (>= 500ms), only when hasPhotos is true. */
  analyze: []
  /** Fired when the mic button is tapped. Parent toggles voice recording state. */
  toggleMic: []
  /** Fired when the QR scan button is tapped. Parent toggles scanner overlay. */
  toggleScan: []
}>()

/** Duration in ms before a press becomes a long press. */
const LONG_PRESS_MS = 500

/** Timer handle for the long-press detection. Cleared on pointer up/cancel. */
const pressTimer = ref<ReturnType<typeof setTimeout> | null>(null)

/** Whether the current interaction was detected as a long press. Prevents tap-on-release. */
const didLongPress = ref(false)

/**
 * Start the long-press timer on pointer down.
 *
 * @remarks
 * Resets `didLongPress` and starts a 500ms timer. If the timer fires
 * before pointer up, it sets `didLongPress = true` and emits `analyze`
 * (when photos exist). This prevents the pointerup handler from also
 * firing `capture`.
 */
function onPointerDown() {
  if (props.captureBusy) return
  didLongPress.value = false
  pressTimer.value = setTimeout(() => {
    didLongPress.value = true
    if (props.hasPhotos) {
      emit("analyze")
    }
  }, LONG_PRESS_MS)
}

/**
 * Complete the pointer portion of the gesture.
 *
 * @remarks
 * Clears the timer if it is still running. The following click event handles
 * a short press so keyboard and assistive-technology activation work too.
 */
function onPointerUp() {
  if (pressTimer.value) {
    clearTimeout(pressTimer.value)
    pressTimer.value = null
  }
}

/** Capture on normal button activation, unless a long press handled it. */
function onClick() {
  if (props.captureBusy) return
  if (didLongPress.value) {
    didLongPress.value = false
    return
  }

  emit("capture")
}

/**
 * Cancel the gesture if the pointer leaves or is interrupted.
 *
 * @remarks
 * Clears the timer without emitting any event. Prevents accidental
 * captures when the user drags their finger off the shutter.
 */
function onPointerCancel() {
  if (pressTimer.value) {
    clearTimeout(pressTimer.value)
    pressTimer.value = null
  }
}
</script>

<template>
  <div class="flex items-center justify-center gap-8">
    <UButton
      :icon="voiceActive ? ICON.micOff : ICON.mic"
      :aria-label="voiceActive ? 'Stop voice hint' : 'Voice hint'"
      :color="voiceActive ? 'error' : 'neutral'"
      :variant="voiceActive ? 'solid' : 'outline'"
      size="xl"
      square
      class="rounded-full"
      :class="voiceActive ? '' : 'bg-white/80 backdrop-blur-sm'"
      @click="$emit('toggleMic')"
    />

    <button
      aria-label="Capture photo"
      :disabled="captureBusy"
      class="border-default flex h-18 w-18 shrink-0 items-center justify-center rounded-full border-4 shadow-lg transition-transform active:scale-95"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @pointerleave="onPointerCancel"
      @click="onClick"
    >
      <div class="h-14 w-14 rounded-full bg-white" />
    </button>

    <UButton
      :icon="ICON.scanQr"
      aria-label="Scan QR code"
      color="neutral"
      variant="outline"
      size="xl"
      square
      class="rounded-full bg-white/80 backdrop-blur-sm"
      @click="$emit('toggleScan')"
    />
  </div>
</template>
